import { Button, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract, Invoice } from '@/api/generated/models';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { getListInvoicesQueryKey, useCreateInvoice } from '@/api/generated/invoices/invoices';
import { FormModal, ModalFooter, MutationButton, SelectField } from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';

interface CreateInvoiceModalProps {
  opened: boolean;
  onClose: () => void;
}

export const CreateInvoiceModal = ({ opened, onClose }: CreateInvoiceModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const [clientOrgId, setClientOrgId] = useState<string | null>(null);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);

  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  // Faktura smije nastati samo za ugovore gdje je moja organizacija partner
  // (backend odbija createInvoice inače s invoice.notPartner) — klijent dropdown
  // se izvodi iz tog istog, već-suženog seta ugovora, ne iz zasebnog API poziva.
  const partnerContracts = useMemo(
    () => contracts.filter((c) => c.partnerOrgId === user?.organisationId),
    [contracts, user?.organisationId]
  );

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    partnerContracts.forEach((c) => {
      if (c.clientOrgId) map.set(c.clientOrgId, c.clientOrgName ?? c.clientOrgId);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [partnerContracts]);

  // Objekt/Brod se bira NAKON klijenta da suzi listu ugovora prije nego ih korisnik
  // uopće vidi (manje dohvaćenih/prikazanih ugovora za klijente s više brodova).
  const objectOptions = useMemo(() => {
    const map = new Map<string, string>();
    partnerContracts
      .filter((c) => c.clientOrgId === clientOrgId)
      .forEach((c) => {
        if (c.objectId) map.set(c.objectId, formatObjectLabel(c));
      });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [partnerContracts, clientOrgId]);

  const contractOptions = useMemo(
    () =>
      partnerContracts
        .filter((c) => c.clientOrgId === clientOrgId && c.objectId === objectId && c.id)
        .map((c) => ({ value: c.id as string, label: c.contractNumber ?? c.name ?? '' })),
    [partnerContracts, clientOrgId, objectId]
  );

  // Ako nakon suženja postoji točno jedna opcija, odmah se postavlja kao zadana
  // (korisnik ne mora ručno birati očiti jedini izbor) — isto za objekt i ugovor.
  useEffect(() => {
    if (objectOptions.length === 1) {
      setObjectId(objectOptions[0].value);
    }
  }, [objectOptions]);

  useEffect(() => {
    if (contractOptions.length === 1) {
      setContractId(contractOptions[0].value);
    }
  }, [contractOptions]);

  const handleClientChange = (value: string | null) => {
    setClientOrgId(value);
    setObjectId(null);
    setContractId(null);
  };

  const handleObjectChange = (value: string | null) => {
    setObjectId(value);
    setContractId(null);
  };

  const reset = () => {
    setClientOrgId(null);
    setObjectId(null);
    setContractId(null);
  };

  const createMutation = useCreateInvoice({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        const createdId = (created as Invoice | undefined)?.id;
        reset();
        onClose();
        if (createdId) {
          navigate(`/invoices/${createdId}`, {
            state: { savedMessage: t('createInvoice.createSuccess') }
          });
        }
      }
    }
  });

  const handleSubmit = () => {
    if (!contractId) return;
    createMutation.mutate({
      data: {
        contractId,
        weekReportIds: []
      }
    });
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('createInvoice.modalTitle')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
          {t('createInvoice.modalHint')}
        </Text>

        <SelectField
          label={t('createInvoice.fieldClient')}
          placeholder={t('createInvoice.clientPlaceholder')}
          required
          data={clientOptions}
          value={clientOrgId}
          onChange={handleClientChange}
        />
        <SelectField
          label={t('createInvoice.fieldObject')}
          placeholder={
            clientOrgId
              ? t('createInvoice.objectPlaceholder')
              : t('createInvoice.selectClientFirst')
          }
          required
          disabled={!clientOrgId}
          data={objectOptions}
          value={objectId}
          onChange={handleObjectChange}
        />
        <SelectField
          label={t('createInvoice.fieldContract')}
          placeholder={
            objectId ? t('createInvoice.contractPlaceholder') : t('createInvoice.selectObjectFirst')
          }
          required
          disabled={!objectId}
          data={contractOptions}
          value={contractId}
          onChange={setContractId}
        />
        {createMutation.isError && (
          <Text fz={13} style={{ color: 'var(--color-status-danger)' }}>
            {t('createInvoice.error')}
          </Text>
        )}

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton
            isPending={createMutation.isPending}
            isPaused={createMutation.isPaused}
            disabled={!contractId}
            onClick={handleSubmit}
          >
            {t('createInvoice.submit')}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
